import type { Card, Chassis, Director, Driver, SeasonState } from "../../types";
import { isChassis, isDriver } from "../../types";
import { needs } from "../../engine/draft";
import { computeSynergy, historicAffinity, synergyWith } from "../../engine/synergy";
import { directorCarBonus, directorChassisAffinity, INDEPENDENT_AFFINITY } from "../../engine/director";
import { directorPreview } from "../../engine/preview";
import { bind } from "../dom";
import { cardMeta, cardStats, cardTitle, chassisModel, esc, rarBadge } from "../helpers";
import { weatherIcon, weatherIconTitle } from "../icons";
import { t, type AttrLabel } from "../i18n";
import type { Screen, ScreenContext } from "./context";

// The lineup strip shown across both draft phases: the 2 drivers + chassis,
// then the mandatory director slot (empty until the final pick).
function slotsStrip(s: SeasonState): string {
  const T = t();
  const empty = (label: string): string =>
    `<div class="slot"><div class="lbl">${label}</div><div class="val" style="color:var(--dim)">${T.empty}</div><div class="vm">&nbsp;</div></div>`;
  // Once the lineup is complete (the director-pick phase), fold each attribute's
  // synergy bonus into the displayed value — green ▲ delta, reasons on hover —
  // so the synergy a motivator would amplify is visible here, not just on the
  // season screen. Obscured in From-Memory mode, like every other stat.
  const ch = s.slots.chassis;
  const full = !!(s.slots.d1 && s.slots.d2 && ch) && !s.fromMemory;
  const syn = full ? computeSynergy(s.slots.d1 as Driver, s.slots.d2 as Driver, ch as Chassis) : null;
  const reasonText = syn ? syn.reasons.map((r) => T.synReason(r)) : [];
  const A = T.attrs;
  const cell = (a: AttrLabel, base: number, delta: number): string => {
    if (s.fromMemory) return `<span title="${esc(a.full)}">${a.abbr} ?</span>`;
    if (delta > 0) {
      const tip = `${a.full} · +${delta} ${T.fromSynergy}${reasonText.length ? " · " + reasonText.join("; ") : ""}`;
      return `<span class="boost" title="${esc(tip)}">${a.abbr} ${base + delta}<span class="up">▲${delta}</span></span>`;
    }
    return `<span title="${esc(a.full)}">${a.abbr} ${base + delta}</span>`;
  };
  const slot = (label: string, c: Card | null): string => {
    if (!c) return empty(label);
    const val = isDriver(c)
      ? `${esc(c.name)} ${esc(c.team)}`
      : `${esc(c.team)} ${esc(chassisModel(c))}`;
    let vm: string;
    if (isDriver(c)) {
      // Only the driver who actually raced this chassis carries the historic
      // pace + consistency edge; same-nation/teammate consistency is team-wide.
      const aff = full ? historicAffinity(c, ch as Chassis) : { pace: 0, consistency: 0 };
      vm = `${cell(A.pace, c.pace, aff.pace)} · ${cell(A.consistency, c.consistency, (syn?.consistency ?? 0) + aff.consistency)} · ${cell(A.rain, c.rain, 0)}`;
    } else {
      vm = `${cell(A.motor, c.motor, syn?.motor ?? 0)} · ${cell(A.aero, c.aero, 0)}`;
    }
    return `<div class="slot"><div class="lbl">${label}</div><div class="val">${val}</div><div class="vm">${vm}</div></div>`;
  };
  const d = s.slots.director;
  const dslot = d
    ? `<div class="slot"><div class="lbl">${T.director}</div><div class="val">${esc(T.directorName(d.name))}</div><div class="vm">${esc(d.team ?? T.directorIndependent)}</div></div>`
    : empty(T.director);
  return `<div class="slots">${slot(T.driver1, s.slots.d1)}${slot(T.driver2, s.slots.d2)}${slot(T.chassis, s.slots.chassis)}${dslot}</div>`;
}

function rerollRow(s: SeasonState, idx: number): string {
  // Per-card rerolls share the pack's single reroll; once spent, hide them.
  if (s.rerollLeft < 1) return "";
  const c = s.pack[idx]!;
  const T = t();
  let btns = `<button class="lockbtn" id="rr-team-${idx}">${esc(T.rerollByTeam(c.team))}</button>`;
  if (isDriver(c)) {
    btns += `<button class="lockbtn" id="rr-country-${idx}">${esc(T.rerollByCountry(c.country))}</button>`;
  }
  return `<div class="locks">${btns}</div>`;
}

function draftCard(s: SeasonState, idx: number): string {
  const c = s.pack[idx]!;
  const n = needs(s);
  const blocked = (isDriver(c) && n.needDriver < 1) || (isChassis(c) && n.needChassis < 1);
  const tags = blocked || s.fromMemory ? [] : synergyWith(c, s.picked);
  const synHTML = tags.length
    ? `<div class="synrow">${tags.map((tag) => `<span class="syntag">◇ ${esc(t().synTag(tag))}</span>`).join("")}</div>`
    : "";
  // From Memory mode hides rarity: drop the tier badge and the colored border.
  const rarityClass = s.fromMemory ? "" : c.rarity;
  const badge = s.fromMemory ? "" : rarBadge(c.rarity);
  return `<div class="card ${rarityClass} ${tags.length ? "has-syn" : ""}" style="${blocked ? "opacity:.4" : ""}">
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:10px">
        <div class="cinfo">
          <div class="cname">${cardTitle(c)}</div>
          <div class="cmeta">${cardMeta(c)}</div>
          ${synHTML}
        </div>
        <div style="display:flex;flex-direction:column;align-items:flex-end;gap:6px;flex-shrink:0">
          ${badge}
          <div class="cstats">${cardStats(c, s.fromMemory)}</div>
          <button class="btn primary" id="pick${idx}" style="padding:6px 12px;font-size:12px" ${blocked ? "disabled" : ""}>${blocked ? t().slotFull : t().keepThis}</button>
        </div>
      </div>
      ${rerollRow(s, idx)}
    </div>`;
}

/**
 * The season calendar, shown during the director pick. Each track is tagged
 * by pace tier (low-pace street/aero vs power), and the count is summarised —
 * a street-heavy calendar is the signal to hire the qualifying specialist over
 * a race strategist. Drawn up front (beginDraft) so it's available here.
 */
function calendarStrip(s: SeasonState): string {
  const T = t();
  if (!s.calendar.length) return "";
  const tierOf = (pw: number): "low" | "power" | "balanced" =>
    pw <= 0.2 ? "low" : pw >= 0.3 ? "power" : "balanced";
  let low = 0;
  let power = 0;
  const chips = s.calendar
    .map((trk, i) => {
      const tier = tierOf(trk.weights.driverPace);
      if (tier === "low") low++;
      else if (tier === "power") power++;
      // Pre-drawn weather is known up front, so show it on each chip — the
      // wet rounds are part of the puzzle when weighing a rain specialist.
      const wx = s.weather[i];
      const wxIcon = wx
        ? ` <span title="${esc(weatherIconTitle(wx, T))}">${weatherIcon(wx)}</span>`
        : "";
      // Tooltip stays on the same axis as the chip's colour (pace tier), so the
      // two can't disagree. The dominant-weight icon lives on the race header.
      return `<span class="calchip ${tier}" title="${esc(T.paceTierTip(tier))}">${esc(trk.name)}${wxIcon}</span>`;
    })
    .join("");
  return `<div class="calstrip">
      <div class="callbl">${T.calendarTitle} <span class="calcount">${low} ${esc(T.paceTierLabel("low"))} · ${power} ${esc(T.paceTierLabel("power"))}</span></div>
      <div class="calchips">${chips}</div>
      <div class="calleg">${T.calendarLegend}</div>
    </div>`;
}

/**
 * Concrete, lineup-specific preview of what hiring this director does to the
 * player's already-picked drivers + chassis. Surfaces the effect of every
 * archetype — including the motivator, whose synergy boost is otherwise
 * invisible — so the pick is an informed comparison, not a guess. Hidden in
 * From-Memory mode, where stats are deliberately obscured.
 */
function directorPreviewRow(s: SeasonState, d: Director): string {
  const T = t();
  const { d1, d2, chassis } = s.slots;
  if (s.fromMemory || !d1 || !d2 || !chassis) return "";
  const p = directorPreview(d1 as Driver, d2 as Driver, chassis as Chassis, d);
  const score = Math.round(p.scoreDelta);
  const wet = Math.round(p.wetDelta);
  const dnf = Math.round(p.dnfDelta * 100);
  const luck = Math.round((p.luckMult - 1) * 100); // % the luck swing widens (+) or narrows (−)
  const chips: string[] = [];
  // Home/independent affinity lives here too — its multiplier is already folded
  // into the score/wet/DNF numbers above, so this chip just names the source.
  const aff = d.team === null ? INDEPENDENT_AFFINITY : directorChassisAffinity(d, chassis as Chassis);
  const affPct = Math.round((aff - 1) * 100);
  if (affPct > 0) chips.push(`<span class="prevchip good">${T.directorPreviewAffinity(affPct, d.team !== null)}</span>`);
  // Home-car development (+motor/+aero) — its score effect is already in the
  // score chip, but name it so the player sees WHY a home director is special.
  const car = directorCarBonus(d, chassis as Chassis);
  if (car.motor > 0 || car.aero > 0) {
    chips.push(`<span class="prevchip good">${T.directorPreviewCar(car.motor, car.aero)}</span>`);
  }
  if (score !== 0) chips.push(`<span class="prevchip">${T.directorPreviewScore(score)}</span>`);
  if (wet > 0) chips.push(`<span class="prevchip wet">${T.directorPreviewWet(wet)}</span>`);
  if (dnf !== 0) chips.push(`<span class="prevchip ${dnf < 0 ? "good" : "bad"}">${T.directorPreviewDnf(dnf)}</span>`);
  // A wider swing is the gambler's double-edged sword (neither purely good nor
  // bad); a narrower one is the reliable director steadying results.
  if (luck !== 0) chips.push(`<span class="prevchip ${luck < 0 ? "good" : "wet"}">${T.directorPreviewLuck(luck)}</span>`);
  const body = chips.length ? chips.join("") : `<span class="prevnone">${T.directorPreviewNone}</span>`;
  return `<div class="prevrow" title="${esc(T.directorPreviewTip)}"><span class="prevlbl">${T.directorPreviewTitle}</span>${body}</div>`;
}

function directorCard(s: SeasonState, idx: number): string {
  const d = s.directorPack[idx]!;
  const T = t();
  const v = (n: number): string => (s.fromMemory ? "?" : String(n));
  const A = T.directorAttrs;
  // Hover explains what the stat does, not just its name — REL/DEV are new
  // concepts, unlike the self-evident driver/chassis attributes.
  const cell = (a: AttrLabel, n: number, key: "reliability" | "development"): string =>
    `<span title="${esc(T.directorAttrTip(key))}">${a.abbr} <b>${v(n)}</b></span>`;
  const rarityClass = s.fromMemory ? "" : d.rarity;
  const badge = s.fromMemory ? "" : rarBadge(d.rarity);
  return `<div class="card ${rarityClass}">
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:10px">
        <div class="cinfo">
          <div class="cname">${esc(T.directorName(d.name))}</div>
          <div class="cmeta">${esc(d.team ?? T.directorIndependent)}</div>
          <div class="dtrait" title="${esc(T.directorTraitTip(d.signature))}">${esc(T.directorTrait(d.signature))}</div>
        </div>
        <div style="display:flex;flex-direction:column;align-items:flex-end;gap:6px;flex-shrink:0">
          ${badge}
          <div class="cstats">${cell(A.reliability, d.reliability, "reliability")}${cell(A.development, d.devSlope, "development")}</div>
          <button class="btn primary" id="dpick${idx}" style="padding:6px 12px;font-size:12px">${T.keepDirector}</button>
        </div>
      </div>
      ${directorPreviewRow(s, d)}
    </div>`;
}

/** The final, mandatory draft step: hire a team principal from a dedicated pack. */
function directorDraft(ctx: ScreenContext, s: SeasonState): Screen {
  const T = t();
  const cards = s.directorPack.map((_d, i) => directorCard(s, i)).join("");
  const html = `<div class="fade">
      <div class="row"><h2 style="margin:0">${T.directorTitle}</h2></div>
      <p class="sub">${T.directorSub}${s.fromMemory ? ` <span class="crar normal">${T.fromMemoryBadge}</span>` : ""}</p>
      ${slotsStrip(s)}
      ${calendarStrip(s)}
      <div class="cards" style="margin-top:14px">${cards}</div>
      <div class="actions">
        <button class="btn" id="rrDir" ${s.rerollLeft < 1 ? "disabled" : ""}>${T.rerollDirectors(s.rerollLeft)}</button>
        <button class="btn ghost" id="startOver">${s.daily ? T.forfeit : T.startOver}</button>
      </div>
    </div>`;
  return {
    html,
    bind() {
      s.directorPack.forEach((_, i) => bind(`dpick${i}`, "click", () => ctx.actions.onPickDirector(i)));
      bind("rrDir", "click", () => ctx.actions.onRerollDirectors());
      bind("startOver", "click", () => ctx.actions.startOver());
    },
  };
}

/** The card-draft screen: pick drivers + a chassis, then (via directorDraft) a principal. */
export function renderDraft(ctx: ScreenContext): Screen {
  const s = ctx.state.season!;
  const n = needs(s);
  // Once the elenco is complete the draft moves to its final, mandatory step:
  // hiring a team principal from a dedicated director pack.
  if (n.needDirector > 0) return directorDraft(ctx, s);
  const T = t();
  const cards = s.pack.map((_c, i) => draftCard(s, i)).join("");
  const chancePct = Math.round(s.currentChance * 100);
  // Pack-level rarity stays visible even in From Memory mode — it's a hint
  // that helps the player target the right card. Only the PER-CARD rarity
  // (badge + border, in draftCard) is hidden.
  const badge = s.currentPremium
    ? `<span class="crar mythic">${T.premiumPack}</span>`
    : `<span class="crar normal">${T.normalPack}</span>`;
  const meta = `<span class="hint" style="margin:0">${T.draftMeta(chancePct, s.mythicsPicked)}</span>`;
  const stillNeed =
    n.needDriver > 0 && n.needChassis > 0
      ? T.needDriverOrChassis
      : n.needDriver > 0
        ? T.needDrivers(n.needDriver)
        : T.needChassis;
  const html = `<div class="fade">
      <div class="row"><h2 style="margin:0">${T.rollTitle(s.rollNo)}</h2> ${badge}
        ${meta}
      </div>
      <p class="sub">${T.keepOne(stillNeed)}${s.fromMemory ? ` <span class="crar normal">${T.fromMemoryBadge}</span>` : ""}</p>
      ${slotsStrip(s)}
      <div class="cards" style="margin-top:14px">${cards}</div>
      <div class="actions">
        <button class="btn" id="rrPack" ${s.rerollLeft < 1 ? "disabled" : ""}>${T.rerollFive(s.rerollLeft)}</button>
        <button class="btn ghost" id="startOver">${s.daily ? T.forfeit : T.startOver}</button>
      </div>
      <p class="hint">${T.lockHint}</p>
    </div>`;
  return {
    html,
    bind() {
      s.pack.forEach((_, i) => {
        bind(`pick${i}`, "click", () => ctx.actions.onPick(i));
        bind(`rr-team-${i}`, "click", () => ctx.actions.onRerollKeeping(i, "team"));
        bind(`rr-country-${i}`, "click", () => ctx.actions.onRerollKeeping(i, "country"));
      });
      bind("rrPack", "click", () => ctx.actions.onRerollPack());
      bind("startOver", "click", () => ctx.actions.startOver());
    },
  };
}
